import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { getUserRole, hasRole, hasAnyRole } from "config/cognito";

/**
 * ProtectedRoute Component
 *
 * Protects routes that require authentication and optionally specific roles.
 *
 * @param {React.ReactNode} children - The component to render if access is granted
 * @param {string[]} allowedRoles - Array of roles that can access this route (empty = any authenticated user)
 * @param {boolean} requireAuth - Whether authentication is required (default: true)
 */
function ProtectedRoute({ children, allowedRoles = [], requireAuth = true }) {
  const auth = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication (guest demo skips this)
  if (auth.isLoading && !auth.isGuest) {
    return (
      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <SoftTypography variant="h6" color="text">
          Loading...
        </SoftTypography>
      </SoftBox>
    );
  }

  // Show error state if authentication failed
  if (auth.error) {
    return (
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <SoftTypography variant="h5" color="error" mb={2}>
          Authentication Error
        </SoftTypography>
        <SoftTypography variant="body2" color="text">
          {auth.error.message}
        </SoftTypography>
      </SoftBox>
    );
  }

  // Redirect to sign-in if not authenticated (guest demo counts as access)
  if (requireAuth && !auth.isAuthenticated && !auth.isGuest) {
    // Save the attempted location for redirect after login
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // If no roles specified, allow any authenticated user
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check if user has required role
  const userRole = getUserRole(auth.user);
  const hasAccess = hasAnyRole(auth.user, allowedRoles);

  if (!hasAccess) {
    // Redirect to unauthorized page or user's default portal
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default ProtectedRoute;
